@if (isOpen()) {
  <!-- Backdrop -->
  <div class="command-palette-backdrop" (click)="close()"></div>

  <!-- Panel -->
  <div
    class="command-palette-panel bg-card"
    role="dialog"
    aria-modal="true"
    aria-label="Quick access"
  >
    <!-- Search field -->
    <div
      class="command-palette-search flex h-14 flex-none items-center gap-3 border-b px-5"
    >
      <mat-icon
        class="text-hint icon-size-5 flex-none"
        [svgIcon]="'heroicons_outline:magnifying-glass'"
      ></mat-icon>

      <!--
        The project filter, as a chip in front of the caret.
        Always removable — an inherited filter that could not be taken off read
        as a permanent label, and left no way to ask about another project.
      -->
      @if (scopeApp(); as scope) {
        <span class="command-palette-scope flex-none">
          <span class="truncate">{{ scope.name }}</span>
          <button
            type="button"
            class="command-palette-scope-clear"
            aria-label="Search all projects"
            title="Search all projects"
            (click)="clearScope()"
          >
            <mat-icon
              class="icon-size-3"
              [svgIcon]="'heroicons_solid:x-mark'"
            ></mat-icon>
          </button>
        </span>
      }

      <!--
        Inline completion, same mechanic as the projects page and the project
        switcher: a dimmed ghost sits *behind* the input showing the full
        suggestion, and the typed characters paint over its head. Same font,
        same box, so the glyphs line up. Tab or Right-arrow accepts.
      -->
      <span class="command-palette-input-wrap min-w-0 flex-auto">
        <span class="command-palette-ghost" aria-hidden="true">{{
          suggestion()
        }}</span>
        <input
          #queryInput
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls="command-palette-results"
          aria-label="Type a command or search"
          autocomplete="off"
          spellcheck="false"
          placeholder="Type a command or search…"
          [value]="query()"
          [attr.aria-activedescendant]="
            activeIndex() >= 0 ? 'command-palette-item-' + activeIndex() : null
          "
          (input)="onQueryInput($event)"
        />
      </span>

      <!--
        The `esc` hint doubles as the close button. It already looked like the
        affordance and sat exactly where a dialog's × goes, so wiring it up
        beats adding a second control that says the same thing twice.
      -->
      <button
        type="button"
        class="command-palette-close flex-none"
        aria-label="Close quick access"
        (click)="close()"
      >
        <kbd>esc</kbd>
      </button>
    </div>

    <!-- Results -->
    <div
      class="command-palette-results"
      id="command-palette-results"
      role="listbox"
      aria-label="Results"
      [class.command-palette-results-busy]="busy()"
    >
      @for (group of groups(); track group.title) {
        <div class="command-palette-group">{{ group.title }}</div>

        @for (item of group.items; track item.command.id) {
          @if (!query().trim() && item.command.separatorBefore) {
            <div class="command-palette-divider" aria-hidden="true"></div>
          }
          <button
            class="command-palette-item"
            type="button"
            role="option"
            [id]="'command-palette-item-' + item.index"
            [class.command-palette-item-active]="item.index === activeIndex()"
            [class.command-palette-item-disabled]="item.command.disabled"
            [attr.aria-disabled]="item.command.disabled || null"
            [attr.aria-selected]="item.index === activeIndex()"
            (mouseenter)="highlight(item.index)"
            (click)="run(item.command)"
          >
            <mat-icon
              class="text-hint icon-size-5 flex-none"
              [svgIcon]="item.command.icon"
            ></mat-icon>

            <span class="min-w-0 flex-auto truncate leading-5">
              {{ item.command.title }}
            </span>

            @if (busy() && busyCommandId() === item.command.id) {
              <mat-spinner
                class="command-palette-item-spinner flex-none"
                diameter="16"
                aria-label="Opening"
              ></mat-spinner>
            }

            @if (item.command.disabled && item.index === activeIndex()) {
              <span class="command-palette-item-hint flex-none text-xs">
                {{ item.command.disabledHint }}
              </span>
            } @else if (item.meta && busyCommandId() !== item.command.id) {
              <span class="text-hint flex-none truncate text-xs">
                {{ item.meta }}
              </span>
            }

            @if (item.command.url) {
              <mat-icon
                class="text-hint icon-size-4 flex-none"
                [svgIcon]="'heroicons_outline:arrow-top-right-on-square'"
              ></mat-icon>
            } @else if (item.command.scopeAppId) {
              <mat-icon
                class="text-hint icon-size-4 flex-none"
                [svgIcon]="'heroicons_solid:chevron-right'"
              ></mat-icon>
            }
          </button>
        }
      } @empty {
        <!--
          Naming the project that is filtering the results is the whole point
          here: "no matches" on its own leaves the reader to work out that they
          are only searching one project.
        -->
        <div class="text-secondary px-5 py-10 text-center">
          @if (scopeApp(); as scope) {
            <div>No matches for “{{ query() }}” in {{ scope.name }}</div>
            <div class="text-hint mt-1 text-sm">
              Press <kbd>&#9003;</kbd> to search all projects
            </div>
          } @else {
            No matches for “{{ query() }}”
          }
        </div>
      }
    </div>

    <!-- Hints -->
    <div
      class="command-palette-footer text-hint flex h-9 flex-none items-center gap-4 border-t px-5 text-xs"
    >
      <span class="flex items-center gap-1">
        <kbd>&#8593;</kbd><kbd>&#8595;</kbd> navigate
      </span>
      <span class="flex items-center gap-1"> <kbd>&#8629;</kbd> open </span>
      <span class="hidden items-center gap-1 sm:flex">
        <kbd>&#8677;</kbd> complete
      </span>
      @if (scopeApp()) {
        <span class="hidden items-center gap-1 sm:flex">
          <kbd>&#9003;</kbd> all projects
        </span>
      }
      <span
        class="command-palette-shortcut-hint ml-auto flex items-center gap-1"
      >
        <kbd>{{ shortcutLabel }}</kbd>
      </span>
    </div>
  </div>
}
